/**
 * Transaction Form Component
 *
 * Smart form with AI-powered category suggestion.
 * When Note field loses focus, calls AI to suggest category.
 */

import { useState, useCallback } from "react";
import { useCreateTransaction, useSuggestCategory } from "../../lib/queries";
import { useAIKey } from "../../hooks/useAIKey";

// ============================================================================
// Utility: Safe amount conversion (avoids floating point precision issues)
// ============================================================================

/**
 * Convert yuan string to cents integer safely
 * Handles floating point precision by parsing string directly
 * 
 * @example
 * safeYuanToCents("19.99") => 1999 (not 1998 or 2000)
 * safeYuanToCents("100") => 10000
 * safeYuanToCents("0.01") => 1
 */
const safeYuanToCents = (yuanStr: string): number => {
    // Remove any whitespace
    const cleaned = yuanStr.trim();

    // Handle empty or invalid input
    if (!cleaned || isNaN(Number(cleaned))) {
        return 0;
    }

    // Split by decimal point
    const parts = cleaned.split(".");
    const integerPart = parts[0] || "0";
    let decimalPart = parts[1] || "00";

    // Pad or truncate decimal to exactly 2 digits
    if (decimalPart.length < 2) {
        decimalPart = decimalPart.padEnd(2, "0");
    } else if (decimalPart.length > 2) {
        // Round the third digit
        const thirdDigit = parseInt(decimalPart[2], 10);
        let cents = parseInt(decimalPart.substring(0, 2), 10);
        if (thirdDigit >= 5) {
            cents += 1;
        }
        decimalPart = cents.toString().padStart(2, "0");
    }

    // Combine as integer
    const cents = parseInt(integerPart, 10) * 100 + parseInt(decimalPart, 10);

    // Handle negative numbers
    return cleaned.startsWith("-") ? -Math.abs(cents) : cents;
};

/**
 * Convert cents to yuan display string
 */
const centsToYuanDisplay = (cents: number): string => {
    return (cents / 100).toFixed(2);
};

// ============================================================================
// Types
// ============================================================================

interface TransactionFormProps {
    ledgerId: string;
    categories: Array<{
        id: string;
        name: string;
        type: "income" | "expense";
    }>;
    onSuccess?: () => void;
    onCancel?: () => void;
}

interface FormData {
    amount: string;
    date: string;
    note: string;
    categoryId: string;
    type: "income" | "expense";
}

// ============================================================================
// Component
// ============================================================================

export const TransactionForm = ({
    ledgerId,
    categories,
    onSuccess,
    onCancel,
}: TransactionFormProps) => {
    const createTransaction = useCreateTransaction();
    const suggestCategory = useSuggestCategory();
    const { isConfigured: hasAIKey } = useAIKey();

    const [form, setForm] = useState<FormData>({
        amount: "",
        // Use local date format (YYYY-MM-DD) instead of UTC
        // toISOString() uses UTC which can show "yesterday" at midnight in UTC+8
        date: new Date().toLocaleDateString('en-CA'), // en-CA format: YYYY-MM-DD
        note: "",
        categoryId: "",
        type: "expense",
    });

    const [suggestion, setSuggestion] = useState<{
        categoryId: string;
        confidence: number;
    } | null>(null);

    const [errors, setErrors] = useState<Partial<FormData>>({});

    // Filter categories by type
    const filteredCategories = categories.filter((c) => c.type === form.type);

    // Handle note blur - trigger AI suggestion
    const handleNoteBlur = useCallback(async () => {
        if (!hasAIKey || !form.note.trim() || form.note.length < 3) {
            return;
        }

        try {
            const result = await suggestCategory.mutateAsync({ note: form.note });

            const response = result as {
                success: boolean;
                data?: {
                    suggestions: Array<{
                        categoryId: string;
                        confidence: number;
                    }>;
                };
            };

            if (response.success && response.data?.suggestions.length) {
                const topSuggestion = response.data.suggestions[0];
                setSuggestion(topSuggestion);

                // Auto-fill if confidence > 0.8 and no category selected
                if (topSuggestion.confidence > 0.8 && !form.categoryId) {
                    setForm((prev) => ({
                        ...prev,
                        categoryId: topSuggestion.categoryId,
                    }));
                }
            }
        } catch (error) {
            // Silently fail - AI is optional
            console.warn("Category suggestion failed:", error);
        }
    }, [form.note, form.categoryId, hasAIKey, suggestCategory]);

    // Validate form
    const validate = (): boolean => {
        const newErrors: Partial<FormData> = {};

        if (!form.amount || parseFloat(form.amount) <= 0) {
            newErrors.amount = "请输入有效金额";
        }

        if (!form.date) {
            newErrors.date = "请选择日期";
        }

        if (!form.categoryId) {
            newErrors.categoryId = "请选择分类";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Handle submit
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validate()) return;

        try {
            // Convert amount and apply sign based on type
            // Expenses are NEGATIVE, Income is POSITIVE
            const cents = safeYuanToCents(form.amount);
            const signedAmount = form.type === "expense" ? -cents : cents;

            await createTransaction.mutateAsync({
                ledgerId,
                categoryId: form.categoryId,
                amount: signedAmount,
                date: new Date(form.date),
                note: form.note || undefined,
            });

            // Reset form
            setForm({
                amount: "",
                date: new Date().toISOString().split("T")[0],
                note: "",
                categoryId: "",
                type: "expense",
            });
            setSuggestion(null);

            onSuccess?.();
        } catch (error) {
            console.error("Failed to create transaction:", error);
        }
    };

    // Get category name by ID
    const getCategoryName = (id: string): string => {
        return categories.find((c) => c.id === id)?.name || "";
    };

    return (
        <form className="transaction-form" onSubmit={handleSubmit}>
            {/* Type Toggle */}
            <div className="form-group">
                <div className="type-toggle">
                    <button
                        type="button"
                        className={`type-toggle-btn ${form.type === "expense" ? "active" : ""}`}
                        onClick={() => setForm((prev) => ({ ...prev, type: "expense", categoryId: "" }))}
                    >
                        支出
                    </button>
                    <button
                        type="button"
                        className={`type-toggle-btn ${form.type === "income" ? "active" : ""}`}
                        onClick={() => setForm((prev) => ({ ...prev, type: "income", categoryId: "" }))}
                    >
                        收入
                    </button>
                </div>
            </div>

            {/* Amount */}
            <div className="form-group">
                <label className="form-label">金额</label>
                <div className="amount-input-wrapper">
                    <span className="amount-prefix">¥</span>
                    <input
                        type="number"
                        className={`form-input amount-input ${errors.amount ? "error" : ""}`}
                        placeholder="0.00"
                        step="0.01"
                        min="0"
                        value={form.amount}
                        onChange={(e) => setForm((prev) => ({ ...prev, amount: e.target.value }))}
                    />
                </div>
                {errors.amount && <span className="form-error">{errors.amount}</span>}
            </div>

            {/* Date */}
            <div className="form-group">
                <label className="form-label">日期</label>
                <input
                    type="date"
                    className={`form-input ${errors.date ? "error" : ""}`}
                    value={form.date}
                    onChange={(e) => setForm((prev) => ({ ...prev, date: e.target.value }))}
                />
                {errors.date && <span className="form-error">{errors.date}</span>}
            </div>

            {/* Note - with AI suggestion on blur */}
            <div className="form-group">
                <label className="form-label">
                    备注
                    {hasAIKey && (
                        <span className="form-label-hint">💡 AI 智能分类</span>
                    )}
                </label>
                <input
                    type="text"
                    className="form-input"
                    placeholder="例如：午餐、地铁"
                    value={form.note}
                    onChange={(e) => setForm((prev) => ({ ...prev, note: e.target.value }))}
                    onBlur={handleNoteBlur}
                />
                {suggestCategory.isPending && (
                    <span className="form-hint">🔄 AI 分析中...</span>
                )}
                {suggestion && (
                    <div className="ai-suggestion">
                        <span className="ai-suggestion-icon">✨</span>
                        <span>
                            AI 建议：<strong>{getCategoryName(suggestion.categoryId)}</strong>
                            <span className="ai-suggestion-confidence">
                                ({Math.round(suggestion.confidence * 100)}% 匹配)
                            </span>
                        </span>
                        {form.categoryId !== suggestion.categoryId && (
                            <button
                                type="button"
                                className="ai-suggestion-apply"
                                onClick={() =>
                                    setForm((prev) => ({ ...prev, categoryId: suggestion.categoryId }))
                                }
                            >
                                应用
                            </button>
                        )}
                    </div>
                )}
            </div>

            {/* Category */}
            <div className="form-group">
                <label className="form-label">分类</label>
                <select
                    className={`form-input form-select ${errors.categoryId ? "error" : ""}`}
                    value={form.categoryId}
                    onChange={(e) => setForm((prev) => ({ ...prev, categoryId: e.target.value }))}
                >
                    <option value="">请选择分类</option>
                    {filteredCategories.map((category) => (
                        <option key={category.id} value={category.id}>
                            {category.name}
                        </option>
                    ))}
                </select>
                {errors.categoryId && <span className="form-error">{errors.categoryId}</span>}
            </div>

            {/* Actions */}
            <div className="form-actions">
                {onCancel && (
                    <button type="button" className="form-btn form-btn-secondary" onClick={onCancel}>
                        取消
                    </button>
                )}
                <button
                    type="submit"
                    className="form-btn form-btn-primary"
                    disabled={createTransaction.isPending}
                >
                    {createTransaction.isPending ? "保存中..." : "保存"}
                </button>
            </div>

            {/* Error Message */}
            {createTransaction.isError && (
                <div className="form-error-message">
                    {createTransaction.error instanceof Error
                        ? createTransaction.error.message
                        : "保存失败"}
                </div>
            )}
        </form>
    );
};

export default TransactionForm;
